/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { PenSquare, Search } from 'lucide-react';
import { type ChangeEvent, useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button.js';
import { type ThreadSummary } from '../../lib/api/messaging.js';
import { cn } from '../../lib/utils/cn.js';
import { formatIst } from '../../lib/utils/datetime.js';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  const text = parts.map((p) => p.charAt(0).toUpperCase()).join('');
  return text === '' ? '?' : text;
}

interface ThreadRowProps {
  readonly thread: ThreadSummary;
  readonly selected: boolean;
  readonly onSelect: (id: string) => void;
}

function ThreadRow({ thread, selected, onSelect }: ThreadRowProps): JSX.Element {
  const handleSelect = useCallback(() => {
    onSelect(thread.id);
  }, [onSelect, thread.id]);

  return (
    <li>
      <button
        type="button"
        onClick={handleSelect}
        className={cn(
          'focus-ring flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/60',
          selected && 'bg-[var(--accent-soft)]',
        )}
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--accent-soft)] text-sm font-semibold text-[var(--accent-strong)]">
          {initials(thread.otherUserName)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span className="truncate font-medium text-slate-800 dark:text-white">
              {thread.otherUserName}
            </span>
            <span className="shrink-0 text-xs text-slate-400">
              {formatIst(thread.lastMessageAt, 'd MMM')}
            </span>
          </span>
          <span className="flex items-center justify-between gap-2">
            <span className="truncate text-sm text-slate-500 dark:text-slate-400">
              {thread.lastMessageBody ?? '—'}
            </span>
            {thread.unreadCount > 0 && (
              <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-[var(--accent)] px-1.5 text-xs font-semibold text-white">
                {thread.unreadCount}
              </span>
            )}
          </span>
        </span>
      </button>
    </li>
  );
}

interface ThreadListProps {
  readonly threads: readonly ThreadSummary[];
  readonly selectedId: string | undefined;
  readonly onSelect: (id: string) => void;
  readonly onNew: () => void;
}

/** Left pane: searchable conversation list with unread badges (Figma Messages). */
export function ThreadList({ threads, selectedId, onSelect, onNew }: ThreadListProps): JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const onSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q === '' ? threads : threads.filter((th) => th.otherUserName.toLowerCase().includes(q));
  }, [threads, query]);

  return (
    <div className="flex w-full max-w-xs flex-col border-r border-slate-200 dark:border-slate-700">
      <div className="flex items-center justify-between border-b border-slate-200 p-4 dark:border-slate-700">
        <h2 className="font-semibold text-slate-800 dark:text-white">{t('messages.title')}</h2>
        <Button size="sm" variant="ghost" onClick={onNew}>
          <PenSquare className="h-4 w-4" aria-hidden="true" />
          {t('messages.new')}
        </Button>
      </div>
      <div className="relative border-b border-slate-100 p-3 dark:border-slate-800">
        <Search
          className="pointer-events-none absolute left-6 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden="true"
        />
        <input
          type="search"
          value={query}
          onChange={onSearch}
          placeholder={t('messages.searchPlaceholder')}
          aria-label={t('messages.searchPlaceholder')}
          className="focus-ring h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
      </div>
      {filtered.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-slate-400">{t('messages.noMatches')}</p>
      ) : (
        <ul className="flex-1 overflow-y-auto">
          {filtered.map((thread) => (
            <ThreadRow
              key={thread.id}
              thread={thread}
              selected={selectedId === thread.id}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
